import React from 'react';
import { Layers, Zap, Info, Scale, Shield, RotateCcw, TrendingUp, AlertCircle, ExternalLink, SlidersHorizontal } from 'lucide-react';
import type { CompanyAnalysis, MarketContext } from '../../types/geminiTypes';
import type { DcfModel } from '../../hooks/useDcfModel';
import type { ValueAssumptions } from '../../types/analysis';
import { cn, coerceToText } from '../../lib/utils';
import { safeToFixed } from '../../lib/formatters';
import { Tooltip } from '../Tooltip';
import ReactMarkdown from 'react-markdown';

export interface ValuationTabProps {
  analysis: CompanyAnalysis;
  dcfModel: DcfModel | null;
  marketContext: MarketContext | null;
  assumptions: ValueAssumptions;
  onAssumptionChange: (key: keyof ValueAssumptions, value: number) => void;
  onResetAssumptions: () => void;
  useForwardMetrics: boolean;
  isForward: boolean;
}

export function ValuationTab({
  analysis,
  dcfModel,
  marketContext,
  assumptions,
  onAssumptionChange,
  onResetAssumptions,
  useForwardMetrics,
  isForward
}: ValuationTabProps) {

  const formatCurrencyValue = (millions: number | undefined | null) => {
    if (millions === undefined || millions === null || isNaN(millions)) return 'N/A';
    if (Math.abs(millions) >= 1000) {
      return `$${safeToFixed(millions / 1000, 1)}B`;
    }
    return `$${safeToFixed(millions, 0)}M`;
  };

  const getSafetyMarginColor = (margin: number) => {
    if (margin >= 30) return "text-success-600 bg-success-50 border-success-200";
    if (margin > 0) return "text-warning-600 bg-warning-50 border-warning-200";
    return "text-danger-500 bg-danger-50 border-danger-200";
  };
  
	const formatNumber = (num: number, opts: any) => {
	  return new Intl.NumberFormat('en-US', opts).format(num);
	};

  const decisionToneClass =
    dcfModel?.decision.action === "PASS" ? "tone-danger"
    : dcfModel?.decision.color === "green" ? "tone-success"
    : dcfModel?.decision.color === "red" ? "tone-danger"
    : "tone-warning";

  let downsidePct = 0;
  let upsidePct = 0;
  let asymmetryRatio: number | null = null;
  let bearScenarioWeight = "Medium";
  let bullScenarioWeight = "Medium";
  let showAsymmetry = false;

  if (dcfModel?.isEstimable && analysis?.currentPrice && analysis.currentPrice > 0) {
    downsidePct = (dcfModel.scenarios.bear - analysis.currentPrice) / analysis.currentPrice;
    upsidePct = (dcfModel.scenarios.bull - analysis.currentPrice) / analysis.currentPrice;
    bearScenarioWeight = analysis.valuationScenarios?.find((s: any) => s.scenario === "Bear")?.probability || "Medium";
    bullScenarioWeight = analysis.valuationScenarios?.find((s: any) => s.scenario === "Bull")?.probability || "Medium";
    if (Math.abs(downsidePct) >= 0.005) {
      asymmetryRatio = upsidePct / Math.abs(downsidePct);
    }
    showAsymmetry = true;
  }

  return (
    <div className="space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Tooltip content={dcfModel.fairValueRange.rationale}>
                  <div className="oi-card p-4 md:p-8 cursor-help group">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="oi-stat-label tracking-[0.2em]">
                        Intrinsic Value {analysis.fmpDcf?.customDcf > 0 ? "(Anchored on Pro Reference)" : ""}
                      </h3>
                      <div className={cn(
                        "px-2 py-0.5 rounded-sm text-[8px] font-medium uppercase tracking-widest border",
                        dcfModel.valuationStatus.statusColor === "green" && "bg-[var(--color-success-50)] text-[var(--color-success-700)] border-[var(--color-success-100)]",
                        dcfModel.valuationStatus.statusColor === "red" && "bg-[var(--color-danger-50)] text-[var(--color-danger-700)] border-[var(--color-danger-100)]",
                        dcfModel.valuationStatus.statusColor === "amber" && "bg-[var(--color-warning-50)] text-[var(--color-warning-700)] border-[var(--color-warning-100)]"
                      )}>
                        {dcfModel.valuationStatus.label}
                      </div>
                    </div>
                    <div className="text-4xl oi-figure tracking-tight">
                      {dcfModel.isEstimable ? 
                        `$${formatNumber(dcfModel.intrinsicValuePerShare, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` :
                        "Not Meaningfully Estimable"
                      }
                    </div>
                    <div className="flex flex-col gap-1 mt-3">
                      <div className="text-[10px] text-ink-600 font-medium">
                        {dcfModel.isEstimable ? (analysis.fmpDcf?.customDcf > 0 ? "Anchored on Normalized Earnings" : "Per Share (Base Case)") : analysis.valuationRationale}
                      </div>
                      {dcfModel.isEstimable && (
                        <>
                          {/* DEDUPE DECISION: Removed "Downside Value Range" and "Strategic Upside" rows as they overlap conceptually with the Bear and Bull scenario values shown here, eliminating visual noise of 4 stacked ranges. */}
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-ink-200 justify-between">
                            <div className="text-[9px] font-medium uppercase tracking-widest text-success-600">Bull Case</div>
                            <div className="text-[10px] oi-figure text-success-600">
                              ${formatNumber(dcfModel.scenarios.bull, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-1 pt-1 justify-between">
                            <div className="text-[9px] font-medium uppercase tracking-widest text-ink-600">Base Case</div>
                            <div className="text-[10px] oi-figure text-ink font-semibold">
                              ${formatNumber(dcfModel.scenarios.base, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-1 pt-1 justify-between">
                            <div className="text-[9px] font-medium uppercase tracking-widest text-danger-600">Bear Case</div>
                            <div className="text-[10px] oi-figure text-danger-600">
                              ${formatNumber(dcfModel.scenarios.bear, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-ink-200">
                            <div className="text-[9px] font-medium uppercase tracking-widest text-ink-500">Fair Value Range:</div>
                            <div className="text-[10px] oi-figure">
                              ${dcfModel.fairValueRange.low.toFixed(2)} - ${dcfModel.fairValueRange.high.toFixed(2)}
                            </div>
                          </div>
                          <div className="text-[8px] text-ink-500 uppercase tracking-widest mt-1">
                            ±{dcfModel.fairValueRange.errorPercent.toFixed(1)}% Estimation Error
                          </div>
                          {analysis.sensitivities && (
                            <div className="mt-2 pt-2 border-t border-ink-100 space-y-1">
                              <div className="flex justify-between items-center">
                                <span className="text-[8px] font-medium uppercase tracking-widest text-ink-500">± 1% WACC →</span>
                                <span className="text-[9px] oi-figure text-ink-600">
                                  ∓ ${Math.abs(analysis.sensitivities.ivDeltaPerWaccPlus1).toFixed(2)} / share
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-[8px] font-medium uppercase tracking-widest text-ink-500">± 2% growth →</span>
                                <span className="text-[9px] oi-figure text-ink-600">
                                  ± ${Math.abs(analysis.sensitivities.ivDeltaPerGrowthPlus2).toFixed(2)} / share
                                </span>
                              </div>
                            </div>
                          )}
                          <div className="mt-4 pt-4 border-t border-ink-200">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-surface-2 border border-ink-200">
                              <div className={cn(
                                "w-1 h-1 rounded-full animate-pulse",
                                analysis.currentPrice > dcfModel.scenarios.base ? "bg-success-600" : analysis.currentPrice > dcfModel.scenarios.bear ? "bg-oxblood" : "bg-danger-600"
                              )} />
                              <span className="text-[8px] font-medium uppercase tracking-widest text-ink-500">
                                Implies: <span className={cn(
                                  "font-semibold",
                                  analysis.currentPrice > dcfModel.scenarios.base ? "text-success-600" : analysis.currentPrice > dcfModel.scenarios.bear ? "text-oxblood" : "text-danger-600"
                                )}>{analysis.currentPrice > dcfModel.scenarios.base ? "Upside Case" : analysis.currentPrice > dcfModel.scenarios.bear ? "Base Value" : "Downside Case"}</span>
                              </span>
                            </div>
                            {showAsymmetry && (
                               <div className="space-y-2 mt-3">
                                <div className="flex justify-between items-start text-success-600">
                                  <div className="flex flex-col">
                                    <span className="text-[9px] font-medium uppercase tracking-widest leading-none">Upside</span>
                                    <span className="text-[8px] opacity-70 mt-0.5">Scenario weight: {bullScenarioWeight} (model judgment)</span>
                                  </div>
                                  <div className="text-[10px] oi-figure text-right">
                                    {upsidePct > 0 ? "+" : ""}{(upsidePct * 100).toFixed(1)}%
                                    <div className="text-[8px] text-ink-500 font-sans opacity-70 mt-0.5">vs ${formatNumber(analysis.currentPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                  </div>
                                </div>
                                
                                <div className="flex justify-between items-start text-danger-600">
                                  <div className="flex flex-col">
                                    <span className="text-[9px] font-medium uppercase tracking-widest leading-none">Downside</span>
                                    <span className="text-[8px] opacity-70 mt-0.5">Scenario weight: {bearScenarioWeight} (model judgment)</span>
                                  </div>
                                  <div className="text-[10px] oi-figure text-right">
                                    {downsidePct > 0 ? "+" : ""}{(downsidePct * 100).toFixed(1)}%
                                  </div>
                                </div>
                                
                                {asymmetryRatio !== null && (
                                  <div className="mt-2 pt-2 border-t border-ink-100 flex justify-between items-center text-ink-600">
                                    <span className="text-[9px] font-medium uppercase tracking-widest">Asymmetry</span>
                                    <span className="text-[10px] oi-figure">Risk/Reward ~ {asymmetryRatio.toFixed(1)}x</span>
                                  </div>
                                )}
                                <div className="text-[7px] text-ink-400 italic leading-tight mt-1">
                                  * Scenario weights represent qualitative AI confidence.
                                </div>
                               </div>
                            )}
                          </div>
                        </>
                      )}
                      <p className="text-[8px] text-ink-500 mt-2 leading-relaxed italic group-hover:text-ink-600 transition-colors">
                        {dcfModel.isEstimable ? dcfModel.fairValueRange.rationale : analysis.valuationRationale}
                      </p>
                    </div>
                  </div>
                </Tooltip>
                <Tooltip content={dcfModel.buyUnderRationale}>
                  <div className="bg-oxblood text-paper p-4 md:p-8 rounded-lg relative overflow-hidden cursor-help">
                    <h3 className="text-[10px] font-medium text-paper/70 uppercase tracking-[0.2em] mb-2 relative z-10">Buy Under Price</h3>
                    <div className="text-4xl oi-figure text-paper tracking-tight relative z-10">${formatNumber(dcfModel.buyUnderPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    <div className="text-[10px] opacity-60 mt-2 font-medium relative z-10">
                      {Math.round(dcfModel.effectiveMarginOfSafety)}% Margin of Safety (adjusted)
                    </div>
                    <p className="text-[8px] text-paper/60 mt-4 leading-relaxed relative z-10 italic group-hover:text-paper/80 transition-colors">
                      {dcfModel.buyUnderRationale}
                    </p>
                  </div>
                </Tooltip>
                <Tooltip content={dcfModel.decision.reason}>
                  <div className={cn(
                    "p-4 md:p-8 rounded-lg flex flex-col justify-between transition-all duration-500 relative overflow-hidden group",
                    decisionToneClass
                  )}>
                    <div className="space-y-6 relative z-10">
                      <div>
                        <h3 className="text-[9px] font-medium uppercase tracking-[0.2em] mb-2 opacity-70">Classification</h3>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-current" />
                          <div className="text-xl font-serif font-medium tracking-tight">
                            {dcfModel.valuationStatus.label}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-[9px] font-medium uppercase tracking-[0.2em] mb-2 opacity-70">Decision</h3>
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "font-mono font-medium tracking-tight tabular-nums",
                            dcfModel.decision.action === "ACCUMULATE" ? "text-3xl" : "text-5xl"
                          )}>{dcfModel.decision.action}</div>
                          <div className="h-8 w-[1px] bg-current opacity-30" />
                          <div className="flex flex-col">
                            <div className="text-[8px] font-medium uppercase tracking-widest opacity-70">
                              {dcfModel.decision.action === "PASS" && dcfModel.decision.color === "red" ? "Current Premium" :
                               dcfModel.decision.action === "PASS" ? "Confidence" : 
                               !dcfModel.isEstimable ? "MOS" :
                               ((dcfModel.intrinsicValuePerShare - analysis.currentPrice) / dcfModel.intrinsicValuePerShare) < 0 ? "Current Premium" : "Current MOS"}
                            </div>
                            <div className="text-xl font-mono font-medium tracking-tight tabular-nums">
                              {dcfModel.decision.action === "PASS" && dcfModel.decision.color === "red" 
                                ? `${safeToFixed(Math.abs(((dcfModel.intrinsicValuePerShare - analysis.currentPrice) / dcfModel.intrinsicValuePerShare) * 100), 0)}%`
                                : dcfModel.decision.action === "PASS"
                                ? (analysis.passConfidenceScore ? `${analysis.passConfidenceScore}%` : "N/A")
                                : !dcfModel.isEstimable ? "N/A"
                                : `${safeToFixed(Math.abs(((dcfModel.intrinsicValuePerShare - analysis.currentPrice) / dcfModel.intrinsicValuePerShare) * 100), 0)}%`
                              }
                            </div>
                          </div>
                        </div>
                      </div>

                      {analysis.confidenceScore && (
                        <div>
                          <h3 className="text-[9px] font-medium uppercase tracking-[0.2em] mb-2 opacity-70">Model Integrity</h3>
                          <div className="flex items-center gap-2">
                            <div className="text-xl font-serif font-medium tracking-tight">
                              {analysis.confidenceScore.label} 
                              <span className="ml-2 opacity-60 text-sm font-sans not-italic font-medium">
                                — {analysis.confidenceScore.score > 5 ? `${analysis.confidenceScore.score}%` : `${analysis.confidenceScore.score}/5`}
                              </span>
                            </div>
                          </div>
                          <p className="text-[8px] mt-1 leading-relaxed italic opacity-70">
                            {coerceToText(analysis.confidenceScore.rationale)}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="mt-6 pt-6 border-t border-current/20">
                      <p className="text-[10px] font-medium leading-relaxed italic opacity-80">
                        {dcfModel.decision.action === "PASS" ? "Passing here increases long-term success odds. Most great investing decisions are negative decisions." : dcfModel.decision.reason}
                      </p>
                    </div>
                  </div>
                </Tooltip>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left Column: Assumptions & Valuation */}
                <div className="lg:col-span-8 space-y-8">
                  {/* Assumptions Editor */}
                  <section className="oi-card p-5 md:p-10">
                    <div className="flex items-center justify-between mb-10 pb-4 border-b border-ink-200">
                      <h3 className="oi-section-title">Oracle Assumptions</h3>
                      <button 
                        onClick={onResetAssumptions}
                        className="text-xs font-medium uppercase tracking-[0.2em] text-ink-600 hover:text-ink flex items-center gap-2 transition-colors"
                      >
                        <Zap className="w-4 h-4" /> 
                        <span>Reset to AI Suggested</span>
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                      <div className="space-y-12">
                        <div className="block group">
                          <Tooltip content="Expected annual revenue growth for the next 10 years. Oracle prefers companies with steady, predictable growth.">
                            <div className="flex justify-between items-end mb-6 cursor-help">
                              <span className="text-sm font-medium uppercase tracking-[0.2em] text-ink-600 group-hover:text-ink transition-colors">Revenue Growth (%)</span>
                              <span className="font-mono text-2xl font-medium tracking-tighter text-ink">{Math.round(assumptions.revenueGrowth)}%</span>
                            </div>
                          </Tooltip>
                          <input 
                            type="range" min="0" max="50" step="1"
                            value={assumptions.revenueGrowth}
                            onChange={(e) => onAssumptionChange('revenueGrowth', parseFloat(e.target.value))}
                            aria-label="Revenue Growth"
                            className="oi-slider w-full"
                          />
                        </div>

                        <div className="block group">
                          <Tooltip content="Expected annual margin expansion or contraction. For quality businesses, margins often expand as they scale or shift to higher-margin segments.">
                            <div className="flex justify-between items-end mb-6 cursor-help">
                              <span className="text-sm font-medium uppercase tracking-[0.2em] text-ink-600 group-hover:text-ink transition-colors">Margin Expansion (%)</span>
                              <span className="font-mono text-2xl font-medium tracking-tighter text-ink">{assumptions.marginExpansion > 0 ? "+" : ""}{assumptions.marginExpansion}%</span>
                            </div>
                          </Tooltip>
                          <input 
                            type="range" min="-5" max="5" step="0.1"
                            value={assumptions.marginExpansion}
                            onChange={(e) => onAssumptionChange('marginExpansion', parseFloat(e.target.value))}
                            aria-label="Margin Expansion"
                            className="oi-slider w-full"
                          />
                        </div>

                        <div className="block group">
                          <Tooltip content="The required rate of return. Oracle often uses a standard hurdle rate (like 10%) to compare all investments equally.">
                            <div className="flex justify-between items-end mb-6 cursor-help">
                              <span className="text-sm font-medium uppercase tracking-[0.2em] text-ink-600 group-hover:text-ink transition-colors">Discount Rate (%)</span>
                              <span className="font-mono text-2xl font-medium tracking-tighter text-ink">{Math.round(assumptions.discountRate)}%</span>
                            </div>
                          </Tooltip>
                          <input 
                            type="range" min="5" max="20" step="1"
                            value={assumptions.discountRate}
                            onChange={(e) => onAssumptionChange('discountRate', parseFloat(e.target.value))}
                            aria-label="Discount Rate"
                            className="oi-slider w-full"
                          />
                        </div>
                      </div>

                      <div className="space-y-12">
                        <div className="block group">
                          <Tooltip content="The growth rate expected after the 10-year projection period. This should typically be in line with long-term GDP growth (2-3%).">
                            <div className="flex justify-between items-end mb-6 cursor-help">
                              <span className="text-sm font-medium uppercase tracking-[0.2em] text-ink-600 group-hover:text-ink transition-colors">Terminal Growth (%)</span>
                              <span className="font-mono text-2xl font-medium tracking-tighter text-ink">{Math.round(assumptions.terminalGrowth)}%</span>
                            </div>
                          </Tooltip>
                          <input 
                            type="range" min="0" max="5" step="1"
                            value={assumptions.terminalGrowth}
                            onChange={(e) => onAssumptionChange('terminalGrowth', parseFloat(e.target.value))}
                            aria-label="Terminal Growth"
                            className="oi-slider w-full"
                          />
                        </div>

                        <div className="block group">
                          <Tooltip content="The discount applied to the intrinsic value to account for uncertainty. Oracle looks for a 'huge' margin of safety.">
                            <div className="flex justify-between items-end mb-6 cursor-help">
                              <span className="text-sm font-medium uppercase tracking-[0.2em] text-ink-600 group-hover:text-ink transition-colors">Margin of Safety (%)</span>
                              <span className="font-mono text-2xl font-medium tracking-tighter text-ink">{Math.round(assumptions.marginOfSafety)}%</span>
                            </div>
                          </Tooltip>
                          <input 
                            type="range" min="0" max="50" step="5"
                            value={assumptions.marginOfSafety}
                            onChange={(e) => onAssumptionChange('marginOfSafety', parseFloat(e.target.value))}
                            aria-label="Margin of Safety"
                            className="oi-slider w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Your Scenario — live result from slider assumptions */}
                  {dcfModel.isEstimable && dcfModel.userScenarioIV > 0 && (
                    <section className="oi-card p-5 md:p-8">
                      <div className="flex items-center justify-between mb-4 pb-3 border-b border-ink-200">
                        <h3 className="oi-section-title">Your Scenario</h3>
                        <span className="text-[9px] font-medium uppercase tracking-widest text-ink-500">Adjust sliders above to update</span>
                      </div>
                      <div className="grid grid-cols-3 gap-6">
                        <div className="space-y-1">
                          <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500">Your IV</div>
                          <div className="text-2xl font-mono font-medium text-ink">${formatNumber(dcfModel.userScenarioIV, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500">Your Buy Under</div>
                          <div className="text-2xl font-mono font-medium text-ink">${formatNumber(dcfModel.userScenarioBuyUnder, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500">vs Oracle IV</div>
                          <div className={cn(
                            "text-2xl font-mono font-medium",
                            dcfModel.userScenarioIV >= dcfModel.intrinsicValuePerShare ? "text-success-600" : "text-danger-600"
                          )}>
                            {dcfModel.intrinsicValuePerShare > 0
                              ? `${dcfModel.userScenarioIV >= dcfModel.intrinsicValuePerShare ? "+" : ""}${(((dcfModel.userScenarioIV - dcfModel.intrinsicValuePerShare) / dcfModel.intrinsicValuePerShare) * 100).toFixed(1)}%`
                              : "N/A"
                            }
                          </div>
                        </div>
                      </div>
                      <p className="text-[9px] text-ink-500 mt-4 pt-3 border-t border-ink-200 leading-relaxed">
                        Applies your assumptions to the same DCF math the engine uses. The Oracle IV above also incorporates qualitative premiums for moat durability and optionality not captured in this calculation.
                      </p>
                    </section>
                  )}

                  {/* Valuation References Section */}
                  <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="oi-card p-4 md:p-8">
                      <div className="text-xs font-medium uppercase tracking-[0.2em] text-ink-600 mb-4">Intrinsic Value Basis</div>
                      <div className="text-4xl font-mono font-medium tracking-tighter mb-4">
                        {dcfModel && dcfModel.isEstimable ? 
                          `$${formatNumber(dcfModel.intrinsicValuePerShare, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` :
                          "Not Meaningfully Estimable"
                        }
                      </div>
                      <div className="text-[10px] text-ink-600 font-medium uppercase tracking-widest mb-6">Based on Owner Earnings</div>
                      
                      <div className="space-y-4 pt-6 border-t border-ink-200">
                        <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Historical Growth (FMP)</div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-ink-600">Revenue Growth:</span>
                          <span className={cn("text-sm font-medium", (analysis.growthData?.revenueGrowth || 0) >= 0 ? "text-success-600" : "text-danger-600")}>
                            {safeToFixed((analysis.growthData?.revenueGrowth || 0) * 100, 1)}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-ink-600">Net Income Growth:</span>
                          <span className={cn("text-sm font-medium", (analysis.growthData?.netIncomeGrowth || 0) >= 0 ? "text-success-600" : "text-danger-600")}>
                            {safeToFixed((analysis.growthData?.netIncomeGrowth || 0) * 100, 1)}%
                          </span>
                        </div>
                      </div>

                      {analysis.avgBuybackYield !== undefined && analysis.avgBuybackYield > 0 && (
                        <div className="space-y-4 pt-6 border-t border-ink-200 mt-6">
                          <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Shareholder Yield (5Y Avg)</div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-ink-600">Buyback Yield:</span>
                            <span className="text-sm font-medium text-ink">
                              {safeToFixed(analysis.avgBuybackYield, 1)}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-ink-600">Dividend Yield:</span>
                            <span className="text-sm font-medium text-ink">
                              {safeToFixed(analysis.dividend, 1)}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t border-ink-200">
                            <span className="text-xs font-medium text-ink uppercase tracking-widest">Total Yield:</span>
                            <span className="text-lg font-mono font-medium text-[var(--color-success-700)]">
                              {safeToFixed((analysis.avgBuybackYield || 0) + (analysis.dividend || 0), 1)}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="oi-card p-4 md:p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="text-xs font-medium uppercase tracking-[0.2em] text-ink-600">
                        FMP Professional Reference
                      </div>
                      <div className="group relative">
                        <Info className="w-4 h-4 text-ink-500 cursor-help" />
                        <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-surface-2 border border-ink-200 rounded-md text-[10px] text-ink-600 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-overlay">
                          FMP's DCF uses standardized, automated inputs which often underestimate the durability and growth of elite compounders. Our model integrates qualitative "Oracle Math" for a more realistic assessment.
                          <div className="mt-2 pt-2 border-t border-ink-200">
                            <a href="/api/health" target="_blank" className="text-oxblood hover:underline pointer-events-auto">Run API Connectivity Diagnostic</a>
                          </div>
                        </div>
                      </div>
                    </div>
                      <div className="grid grid-cols-2 gap-8 mb-8">
                        <div className="space-y-2">
                          <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Standard Yield</div>
                          <div className="text-2xl font-mono font-medium">${formatNumber(analysis.fmpDcf?.dcf, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="space-y-2">
                          <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Levered Flow</div>
                          <div className="text-2xl font-mono font-medium">${formatNumber(analysis.fmpDcf?.leveredDcf, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                      </div>

                      {analysis.analystTargetPrice !== undefined && analysis.analystTargetPrice > 0 && (
                        <div className="pt-6 border-t border-ink-200 mb-8">
                          <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">Wall Street Consensus (Alpha Vantage)</div>
                          <div className="text-3xl font-mono font-medium text-success-600">${formatNumber(analysis.analystTargetPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div className="text-[9px] text-ink-500 uppercase tracking-widest mt-2">
                            Average Analyst Price Target
                          </div>
                        </div>
                      )}
                      
                      {analysis.fmpDcf.customDcf > 0 && (
                        <div className="pt-6 border-t border-ink-200 mb-8">
                          <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">Owner Earnings Model</div>
                          <div className="text-3xl font-mono font-medium text-oxblood">${formatNumber(analysis.fmpDcf.customDcf, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div className="text-[9px] text-ink-500 uppercase tracking-widest mt-2">
                            WACC: {analysis.fmpDcf.customDcfData?.wacc?.toFixed(2) || '9.04'}% | LTG: {analysis.fmpDcf.customDcfData?.longTermGrowthRate?.toFixed(2) || '4.00'}%
                          </div>
                        </div>
                      )}

                      <div className="pt-6 border-t border-ink-200">
                        <div className="text-[10px] font-medium uppercase tracking-widest text-ink-500 mb-2">
                          Professional Owner Earnings (TTM)
                        </div>
                        <div className="text-2xl font-mono font-medium text-ink">${formatNumber(analysis.ownerEarningsData?.ownersEarningsPerShare, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / share</div>
                        <div className="text-[9px] text-ink-500 uppercase tracking-widest mt-2">Total: {formatCurrencyValue(analysis.ownerEarningsData?.ownersEarnings || 0)}</div>
                      </div>
                    </div>
                  </section>

                    {analysis.valuationInsights && (
                      <div className="mt-12 p-4 md:p-8 oi-card">
                        <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-600 mb-4 flex items-center gap-2">
                          <div className="w-1 h-1 bg-oxblood rounded-full" />
                          Deep Valuation Insights
                        </h4>
                        <div className="prose prose-sm max-w-none text-ink-600 leading-relaxed">
                          <ReactMarkdown>{coerceToText(analysis.valuationInsights)}</ReactMarkdown>
                        </div>
                      </div>
                    )}

                    {analysis.sanityCheck && (
                      <div className="mt-8 p-6 tone-warning rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <Scale className="w-4 h-4 text-warning-600" />
                          <div className="text-[10px] font-medium uppercase tracking-[0.2em] opacity-70">"Back of the Envelope" Sanity Check</div>
                        </div>
                        <p className="text-xs opacity-90 leading-relaxed">
                          {coerceToText(analysis.sanityCheck)}
                        </p>
                      </div>
                    )}
                  
                  {/* 10-Year Cash Flow Projection Table */}
                  {/* Removed from here as it's now in the memo */}

                  {/* Investment Memo moved below the grid */}
                </div>
                <div className="lg:col-span-4 space-y-8">
                  {/* Investment Archetype */}
                  {analysis.archetype && (
                    <section className="oi-card p-4 md:p-8">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-md bg-oxblood-50 flex items-center justify-center">
                          {analysis.archetype.category === "YIELD UTILITY / BOND PROXY" && <Shield className="w-4 h-4 text-oxblood" />}
                          {analysis.archetype.category === "YIELD + CONSOLIDATOR" && <Layers className="w-4 h-4 text-oxblood" />}
                          {analysis.archetype.category === "TURNAROUND / RE-INFLECTION CANDIDATE" && <RotateCcw className="w-4 h-4 text-oxblood" />}
                          {analysis.archetype.category === "COMPOUNDER" && <TrendingUp className="w-4 h-4 text-oxblood" />}
                        </div>
                        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500">Investment Archetype</div>
                      </div>
                      <h4 className="text-xl font-serif font-medium italic mb-3">{coerceToText(analysis.archetype.category)}</h4>
                      <p className="text-xs text-ink-600 leading-relaxed italic">{coerceToText(analysis.archetype.rationale)}</p>
                    </section>
                  )}

                  {/* Business Quality vs. Investment Attractiveness (Dashboard) */}
                  {analysis.businessQualityVsInvestmentAttractiveness && (
                    <section className="oi-card p-4 md:p-8 space-y-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-md bg-oxblood-50 flex items-center justify-center">
                          <Layers className="w-4 h-4 text-oxblood" />
                        </div>
                        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-ink-500">Quality vs. Attractiveness</div>
                      </div>
                      <div className="space-y-4">
                        <div className="p-4 bg-surface-2 rounded-md border border-ink-200">
                          <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500 mb-2">Business Quality</div>
                          <p className="text-xs text-ink leading-relaxed">
                            {coerceToText(analysis.businessQualityVsInvestmentAttractiveness.businessQuality)}
                          </p>
                        </div>
                        <div className="p-4 bg-surface-2 rounded-md border border-ink-200">
                          <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500 mb-2">Investment Attractiveness</div>
                          <p className="text-xs text-ink leading-relaxed">
                            {coerceToText(analysis.businessQualityVsInvestmentAttractiveness.investmentAttractiveness)}
                          </p>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* Mistake Prevention (Dashboard) */}
                  {analysis.mistakePrevention && analysis.mistakePrevention.length > 0 && (
                    <section className="tone-warning p-4 md:p-8 rounded-lg space-y-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-md bg-[var(--color-warning-100)] flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-warning-600" />
                        </div>
                        <div className="text-[10px] font-medium uppercase tracking-[0.2em] opacity-70">Mistake Prevention</div>
                      </div>
                      <div className="space-y-4">
                        {analysis.mistakePrevention?.slice(0, 2).map((mistake, idx) => (
                          <div key={idx} className="space-y-1">
                            <div className="text-[9px] font-medium uppercase tracking-widest">{coerceToText(mistake.title)}</div>
                            <p className="text-[10px] opacity-80 leading-relaxed">{coerceToText(mistake.description)}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Engine Parameters */}
                  <section className="oi-card p-5 md:p-10 overflow-hidden">
                    <div className="mb-6 pb-4 border-b border-ink-200">
                      <h3 className="oi-section-title">Engine Parameters</h3>
                      <p className="text-[9px] text-ink-500 mt-2 leading-relaxed">The inputs Oracle's DCF engine used to derive the base-case intrinsic value.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-8">
                      <div className="space-y-2">
                        <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500">Owner Earnings / Share</div>
                        <div className="text-lg font-mono font-medium text-ink">${formatNumber(dcfModel.fcfPerShare, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500">Engine WACC</div>
                        <div className="text-lg font-mono font-medium text-ink">
                          {analysis.dcfAssumptions?.wacc != null
                            ? `${(analysis.dcfAssumptions.wacc * 100).toFixed(1)}%`
                            : `${formatNumber(assumptions.discountRate + (dcfModel.premiums?.qualityDiscount || 0), { maximumFractionDigits: 1 })}%`
                          }
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500">Terminal Growth</div>
                        <div className="text-lg font-mono font-medium text-ink">
                          {analysis.dcfAssumptions?.terminalGrowthRate != null
                            ? `${(analysis.dcfAssumptions.terminalGrowthRate * 100).toFixed(1)}%`
                            : `${formatNumber(dcfModel.premiums?.terminalGrowth || 0, { maximumFractionDigits: 1 })}%`
                          }
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500">Terminal Multiple</div>
                        <div className="text-lg font-mono font-medium text-ink">
                          {analysis.dcfAssumptions?.terminalMultiple != null
                            ? `${analysis.dcfAssumptions.terminalMultiple}x`
                            : `${dcfModel.exitMultiple || 15}x`
                          }
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Data Sources */}
                  {analysis.sources && analysis.sources.length > 0 && (
                    <section className="oi-card p-5 md:p-10">
                      <div className="mb-6 pb-4 border-b border-ink-200">
                        <h3 className="oi-section-title">Data Sources</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {analysis.sources.map((source, idx) => (
                          <a 
                            key={idx} 
                            href={source.uri} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-4 bg-surface-2 rounded-md border border-ink-200 hover:bg-surface transition-all group"
                          >
                            <div className="w-8 h-8 rounded-md bg-surface flex items-center justify-center border border-ink-200 group-hover:border-oxblood/30 transition-colors">
                              <ExternalLink className="w-3.5 h-3.5 text-ink-500 group-hover:text-ink" />
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="text-[10px] font-medium uppercase tracking-widest text-ink-600 truncate">{coerceToText(source.title)}</span>
                              <span className="text-[8px] text-ink-500 truncate">{source.uri}</span>
                            </div>
                          </a>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Production Path moved to bottom */}
                </div>
              </div>
            </div>
  );
}
